/**
 * ESCHER FALLS — 記録の保存と進行状況。METAGAME-SPEC §2 / §3。
 *
 * localStorage に置くが、**保存できなくてもゲームは普通に遊べる**ことを最優先にする。
 * プライベートモードや容量超過で例外が飛んでゲームが起動しない、が最悪の失敗である。
 * 読めない・壊れている・型が違う場合はすべて黙って初期状態に倒す。
 *
 * データを触る関数はすべて**純関数**にしてある（入力を書き換えず新しい値を返す）。
 * こうしておくとテストでき、保存の失敗と状態の更新を切り離せる。
 */

export const RECORDS_KEY = 'escher-falls/records/v1';
export const RECORDS_VERSION = 1;

/** 1面ぶんの記録。 */
export interface LevelRecord {
  /** 自己ベスト (ms)。 */
  readonly bestTimeMs: number;
  /** そのベストを出したときの手数（方位回転＋仰角移動の合計）。 */
  readonly bestMoves: number;
  readonly clearCount: number;
  /** 初回クリア時刻の ISO 文字列。 */
  readonly firstClearedAt: string;
}

/** 進行状況。METAGAME-SPEC §3。 */
export interface Progress {
  /** 何周目か。1 始まり。 */
  readonly loop: number;
  /** クリア済みの本編ステージ id。 */
  readonly clearedMain: readonly string[];
  /**
   * エクストラが開いているか。
   *
   * **かつては「本編を全通水すると開く」褒美だったが、課金での解放に変えた。**
   * すでに開けていた人から取り上げないため、この旗は残して読み続ける
   * （新しく立つことはない。立てるのは `extrasOwned` のほう）。
   */
  readonly unlockedExtra: boolean;
  /** エクストラを購入したか。**いまはこれが唯一の解放手段。** */
  readonly extrasOwned: boolean;
  /**
   * 二周目が開いているか。**本編を全通水した褒美はこちらへ付け替えた。**
   * 課金しない人にも「次」が残るようにするため。
   */
  readonly unlockedLoop2: boolean;
  /**
   * 全25面を通した日時（ISO 文字列）。まだなら `null`。
   *
   * **称号の証拠はここに残る。** 祝いの画面は閉じれば消えるが、
   * 「やり遂げた」という事実が消えてはいけない。ステージ選択の見出しは
   * これを見て称号を出し続ける。
   */
  readonly allClearedAt: string | null;
}

export interface RecordsData {
  readonly version: number;
  readonly levels: Readonly<Record<string, LevelRecord>>;
  readonly progress: Progress;
}

export const EMPTY_RECORDS: RecordsData = {
  version: RECORDS_VERSION,
  levels: {},
  progress: {
    loop: 1,
    clearedMain: [],
    unlockedExtra: false,
    extrasOwned: false,
    unlockedLoop2: false,
    allClearedAt: null,
  },
};

/**
 * localStorage の最小限の面。
 * テストで差し替えられるよう、`Storage` そのものではなくこの形に依存する。
 */
export interface RecordStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * ブラウザの localStorage を返す。使えない環境では `null`。
 *
 * Safari のプライベートモードなどでは、`localStorage` の**参照だけで**例外が飛ぶ。
 * `typeof` で存在を見るだけでは足りないので、実際に読み書きして確かめる。
 */
export function defaultStore(): RecordStore | null {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const probe = '__ef_probe__';
    ls.setItem(probe, '1');
    ls.removeItem(probe);
    return ls;
  } catch {
    return null;
  }
}

// =============================================================================
// 読み書き
// =============================================================================

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function finiteNumber(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

/** 1件ぶんを検証する。1つでも不正なら `null`（その面の記録だけ捨てる）。 */
function parseLevelRecord(v: unknown): LevelRecord | null {
  if (!isObject(v)) return null;
  const bestTimeMs = finiteNumber(v.bestTimeMs, -1);
  if (bestTimeMs < 0) return null;
  return {
    bestTimeMs,
    bestMoves: Math.max(0, finiteNumber(v.bestMoves, 0)),
    clearCount: Math.max(0, finiteNumber(v.clearCount, 1)),
    firstClearedAt: typeof v.firstClearedAt === 'string' ? v.firstClearedAt : '',
  };
}

function parseProgress(v: unknown): Progress {
  if (!isObject(v)) return EMPTY_RECORDS.progress;
  const cleared = Array.isArray(v.clearedMain)
    ? v.clearedMain.filter((x): x is string => typeof x === 'string')
    : [];
  return {
    loop: Math.max(1, finiteNumber(v.loop, 1)),
    clearedMain: cleared,
    unlockedExtra: v.unlockedExtra === true,
    extrasOwned: v.extrasOwned === true,
    unlockedLoop2: v.unlockedLoop2 === true,
    allClearedAt: typeof v.allClearedAt === 'string' ? v.allClearedAt : null,
  };
}

/**
 * 記録を読む。**決して例外を投げない。**
 * 壊れていても、バージョンが未知でも、保存領域が無くても `EMPTY_RECORDS` を返す。
 */
export function loadRecords(store: RecordStore | null = defaultStore()): RecordsData {
  if (store === null) return EMPTY_RECORDS;
  let raw: string | null;
  try {
    raw = store.getItem(RECORDS_KEY);
  } catch {
    return EMPTY_RECORDS;
  }
  if (raw === null || raw === '') return EMPTY_RECORDS;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return EMPTY_RECORDS;
  }
  if (!isObject(parsed)) return EMPTY_RECORDS;
  // 未知のバージョンは移行せず捨てる。中途半端に読むより作り直す方が安全である。
  if (parsed.version !== RECORDS_VERSION) return EMPTY_RECORDS;

  const levels: Record<string, LevelRecord> = {};
  if (isObject(parsed.levels)) {
    for (const [id, value] of Object.entries(parsed.levels)) {
      const rec = parseLevelRecord(value);
      if (rec !== null) levels[id] = rec;
    }
  }

  return { version: RECORDS_VERSION, levels, progress: parseProgress(parsed.progress) };
}

/** 記録を書く。**決して例外を投げない。** 書けたら true。 */
export function saveRecords(
  data: RecordsData,
  store: RecordStore | null = defaultStore(),
): boolean {
  if (store === null) return false;
  try {
    store.setItem(RECORDS_KEY, JSON.stringify(data));
    return true;
  } catch {
    // 容量超過など。保存できないだけで、遊べなくはならない。
    return false;
  }
}

// =============================================================================
// 更新（純関数）
// =============================================================================

/**
 * クリアを記録した新しいデータを返す。**元のデータは書き換えない。**
 *
 * @param mainLevelIds 本編ステージの id（順不同）。全部揃ったら解放判定に使う。
 * @param now 記録時刻。呼び出し側から渡す（テストを決定的にするため）。
 */
export function recordClear(
  data: RecordsData,
  levelId: string,
  timeMs: number,
  moves: number,
  mainLevelIds: readonly string[],
  now: Date,
): RecordsData {
  const prev = data.levels[levelId];
  // ベスト更新のときだけ手数も差し替える。「別々の周回の最良値」を混ぜない。
  const isBest = prev === undefined || timeMs < prev.bestTimeMs;

  const next: LevelRecord = {
    bestTimeMs: isBest ? timeMs : prev.bestTimeMs,
    bestMoves: isBest ? moves : prev.bestMoves,
    clearCount: (prev?.clearCount ?? 0) + 1,
    firstClearedAt: prev?.firstClearedAt || now.toISOString(),
  };

  const clearedMain = data.progress.clearedMain.includes(levelId)
    ? data.progress.clearedMain
    : [...data.progress.clearedMain, levelId];

  const allMainCleared =
    mainLevelIds.length > 0 && mainLevelIds.every((id) => clearedMain.includes(id));

  return {
    version: RECORDS_VERSION,
    levels: { ...data.levels, [levelId]: next },
    progress: {
      ...data.progress,
      clearedMain,
      // 一度解放したものは戻さない。
      // **エクストラはもう全通水では開かない**（課金での解放に変えた）。
      // 過去に開けていた人の旗はそのまま持ち越す。
      unlockedExtra: data.progress.unlockedExtra,
      // 全通水の褒美は二周目へ付け替えた。課金しない人にも次が残る。
      unlockedLoop2: data.progress.unlockedLoop2 || allMainCleared,
    },
  };
}

/**
 * 全面踏破の日時を刻む。**まだ刻まれていないときだけ**書く。
 *
 * `recordClear` に混ぜていないのは、あれが本編の id しか受け取らないため。
 * エクストラを含めた全面の判定は呼び出し側（進行管理）が持っている。
 * ここに全レベルを渡す形にすると、記録の保存が面の一覧に依存してしまう。
 *
 * 一度刻んだら二度と書き換えない。称号は「最初に成し遂げた日」に紐づく。
 */
export function markAllCleared(data: RecordsData, now: Date): RecordsData {
  if (data.progress.allClearedAt !== null) return data;
  return {
    ...data,
    progress: { ...data.progress, allClearedAt: now.toISOString() },
  };
}

/** エクストラを購入した。一度立てたら戻さない。 */
export function grantExtras(data: RecordsData): RecordsData {
  if (data.progress.extrasOwned) return data;
  return { ...data, progress: { ...data.progress, extrasOwned: true } };
}

/** 今回のクリアが自己ベスト更新か（記録する**前**のデータで判定すること）。 */
export function isNewBest(data: RecordsData, levelId: string, timeMs: number): boolean {
  const prev = data.levels[levelId];
  return prev === undefined || timeMs < prev.bestTimeMs;
}

/** 本編の合計ベストタイム。未クリアの面が1つでもあれば `null`。 */
export function totalBestTime(
  data: RecordsData,
  mainLevelIds: readonly string[],
): number | null {
  let total = 0;
  for (const id of mainLevelIds) {
    const rec = data.levels[id];
    if (rec === undefined) return null;
    total += rec.bestTimeMs;
  }
  return total;
}

/** 面が選択可能か。未クリアでも「次の1面」までは選べる。METAGAME-SPEC §3。 */
export function isLevelUnlocked(
  data: RecordsData,
  levelId: string,
  orderedIds: readonly string[],
): boolean {
  const index = orderedIds.indexOf(levelId);
  if (index <= 0) return true; // 1面目は常に選べる
  // 直前の面をクリアしていれば選べる。一度クリアした面は当然ずっと選べる。
  if (data.levels[levelId] !== undefined) return true;
  return data.levels[orderedIds[index - 1]] !== undefined;
}
